/**
 * Stops a mouse wheel from ever changing a number field's value, everywhere in the app.
 *
 * WHY THIS EXISTS. A focused `input[type=number]` steps its value when the wheel turns over it. The
 * user is scrolling the page; the browser reads it as editing. It is silent, it is plausible - the
 * number stays the right order of magnitude - and it lands in the record as a deliberate edit. A
 * frame width typed as 3500 was stored as 3499.3: seven steps of 0.1 that nobody made, on a
 * measurement that certifies a product.
 *
 * It is not a validation problem, because every value it produces is valid. Nothing downstream can
 * distinguish it from a real edit, so it has to be stopped at the input.
 *
 * WHY AT THE DOCUMENT, once. Shoelace controls keep their real input inside a shadow root, so there
 * is no global stylesheet or attribute that reaches them, and the app has no shared control base
 * class to centralise on. A capture-phase listener on the document sees every wheel event before any
 * component does, including those inside shadow roots, and needs no per-field wiring - so a field
 * added next year is covered without anyone remembering this rule. Same reasoning, and the same
 * bootstrap point, as the Shoelace control overrides.
 */

/** Installed once at bootstrap. Returns a disposer, which the tests use. */
export function installNumberInputWheelGuard(
  target: Document = document
): () => void {
  const onWheel = (event: Event) => {
    const input = findFocusedNumberInput(event);
    if (!input) return;

    // Two steps, and both are needed.
    //
    // preventDefault stops THIS tick from stepping the value. blur stops the ones after it: with the
    // field no longer focused the browser has nothing to step, so the page scrolls normally from the
    // second tick on. Blurring alone would very likely be enough - the default action runs after
    // listeners, by which point the field is not focused - but that is a detail of dispatch ordering
    // to lean on for a silent data defect, so the tick is refused outright as well.
    //
    // The cost is one swallowed tick before the page starts moving, paid only when the pointer is
    // over the very field that has focus. Blocking the wheel outright would have been the obvious
    // fix and a worse one: the page would freeze whenever the pointer happened to rest on a focused
    // field, which is most of the time while filling in a form.
    event.preventDefault();
    input.blur();
  };

  target.addEventListener('wheel', onWheel, { capture: true, passive: false });
  return () => target.removeEventListener('wheel', onWheel, { capture: true });
}

/**
 * The number field this wheel event would edit, or null.
 *
 * Reads the composed path rather than `event.target`, which is retargeted to the host element for
 * anything inside a shadow root - every Shoelace input in the app. The focus check is what keeps
 * scrolling normal everywhere else: an unfocused number field does not step, so there is nothing to
 * defend against and no reason to interfere with the page.
 */
function findFocusedNumberInput(event: Event): HTMLInputElement | null {
  const path =
    typeof event.composedPath === 'function' ? event.composedPath() : [];

  for (const node of path) {
    if (!isNumberInput(node)) continue;

    // Focus is tracked per root, so a field inside a shadow root is the activeElement OF THAT ROOT.
    const root = node.getRootNode() as Document | ShadowRoot;
    return root.activeElement === node ? node : null;
  }

  return null;
}

function isNumberInput(node: EventTarget): node is HTMLInputElement {
  return (
    node instanceof HTMLInputElement && node.type.toLowerCase() === 'number'
  );
}
